// ──────────────────────────────────────────────────────
// Admin API Client — Q Care AI Kiosk Admin Panel
// Uses JWT Bearer token auth via admin-auth library
// ──────────────────────────────────────────────────────

import { adminFetch, adminFetchJson } from './admin-auth';
import { normalizeDigits } from './normalize';
import type { CreditStatus } from './types';

// ── Product types ──────────────────────────────────────
export interface Product {
    id: string;
    sku: string | null;
    barcode: string;
    nameAr: string;
    nameEn: string | null;
    brand: string | null;
    category: string | null;
    description: string | null;
    benefits: string[];
    keyBenefitsAr: string[];
    howToUseAr: string | null;
    warningsAr: string | null;
    ingredientsAr: string | null;
    usage: string | null;
    imageUrl: string | null;
    imageSource?: string;
    thumbnailUrl?: string | null;
    primaryImageUrl?: string | null;
    imagesCount?: number;
    price?: number | null;
    taxRate?: number | null;
    stock?: number | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    barcodes: { id: string; barcode: string }[];
    _count?: { upsells: number };
    inactiveReason?: string | null;
    // AI quality signals
    aiReadinessScore?: number | null;
    aiReady?: boolean | null;
    recommendationEligible?: boolean | null;
    enrichmentNeeded?: boolean | null;
    enrichmentGap?: string[] | null;
    enrichmentScore?: number | null;
    enrichmentPriorityScore?: number | null;
    enrichmentPriorityLabel?: 'high' | 'medium' | 'low' | null;
    enrichmentPriorityReason?: string | null;
    statusChangedAt?: string | null;
    lastSeenInFeed?: string | null;
    // Product Intelligence fields
    domain?: string | null;
    productTypeV3?: string | null;
    productTypeSecondary?: string | null;
    genderTarget?: string;
    genderTargetConf?: string;
    skinTypeSuitability?: string[];
    skinTypeSuitabilityConf?: string;
    hairTypeSuitability?: string[];
    hairTypeSuitabilityConf?: string;
    // Safety & Compliance dimension removed (product decision). The
    // corresponding DB columns stay dormant; frontend Product type no longer
    // surfaces sensitivitySafe / containsRetinol / photosensitive /
    // notForPregnant / containsAcids / containsAlcohol / safetyFlagsConf.
    usageTime?: string;
    usageTimeConf?: string;
    routineStep?: string | null;
    routineStepConf?: string;
    concerns?: string[];
    concernsConf?: string;
    priceTier?: string | null;
    erpCategory?: string | null;
    erpProductGroup?: string | null;
    erpSuggestion?: { domain: string; productType: string } | null;
    countryOfOrigin?: string | null;
    purchasePrice?: number | null;
    classificationStatus?: string;
    completenessPct?: number;
    salesRank?: number | null;
    totalSalesRevenue?: number | null;
    // Confidence + source (0-100 int + source string)
    genderTargetConfidence?: number;
    genderTargetSource?: string;
    skinTypeSuitabilityConfidence?: number;
    skinTypeSuitabilitySource?: string;
    hairTypeSuitabilityConfidence?: number;
    hairTypeSuitabilitySource?: string;
    // sensitivitySafeConfidence/Source + safetyFlagsConfidence/Source removed
    usageTimeConfidence?: number;
    usageTimeSource?: string;
    routineStepConfidence?: number;
    routineStepSource?: string;
    concernsConfidence?: number;
    concernsSource?: string;
    // ── Phase 2 — Semantic role taxonomy ──
    primaryRole?: string | null;
    primaryRoleConfidence?: number;
    primaryRoleSource?: string;
    bodyRegion?: string | null;
    bodyRegionConfidence?: number;
    bodyRegionSource?: string;
    sunAction?: string | null;
    sunActionConfidence?: number;
    sunActionSource?: string;
    usageFrequency?: string | null;
    usageFrequencyConfidence?: number;
    usageFrequencySource?: string;
    // Enrichment content (from product_enrichments via include)
    enrichment?: {
        ragSummaryAr?: string | null;
        benefitsAr?: string[];
        howToUseAr?: string[];
        warningsAr?: string[];
        aiContextNotesAr?: string | null;
        descriptionConfidence?: number;
        descriptionSource?: string;
        benefitsConfidence?: number;
        benefitsSource?: string;
        howToUseConfidence?: number;
        howToUseSource?: string;
        warningsConfidence?: number;
        warningsSource?: string;
        expertNotesConfidence?: number;
        expertNotesSource?: string;
    } | null;
}

export interface ProductListResponse {
    items: Product[];
    total: number;
    page: number;
    limit: number;
    enrichmentSummary?: {
        high: number;
        medium: number;
        low: number;
        total: number;
    };
}

// ── Classification API ────────────────────────────────

export interface ClassificationListResponse {
    items: Product[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    statusCounts?: Record<string, number>;
}

export async function listProductClassification(params: {
    page?: number;
    limit?: number;
    search?: string;
    domain?: string;
    productType?: string;
    genderTarget?: string;
    missingFields?: boolean;
    priceTier?: string;
    status?: string;
    sort?: string;
}): Promise<ClassificationListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.domain) query.set('domain', params.domain);
    if (params.productType) query.set('productType', params.productType);
    if (params.genderTarget) query.set('genderTarget', params.genderTarget);
    if (params.missingFields) query.set('missingFields', 'true');
    if (params.priceTier) query.set('priceTier', params.priceTier);
    if (params.status) query.set('status', params.status);
    if (params.sort) query.set('sort', params.sort);

    return adminFetchJson<ClassificationListResponse>(`/admin/products/classification?${query.toString()}`);
}

export interface FillRateField { key: string; label: string; filled: number }
export interface FillRatesResponse { total: number; fields: FillRateField[] }

export async function fetchClassificationFillRates(): Promise<FillRatesResponse> {
    return adminFetchJson<FillRatesResponse>('/admin/products/classification/fill-rates');
}

// ── Product API ────────────────────────────────────────

export async function listProducts(
    params?: {
        query?: string; barcode?: string; page?: number; limit?: number;
        category?: string; status?: string; stock?: string; hasImage?: string;
        aiReady?: string; recEligible?: string; enrichNeeded?: string;
        imageStatus?: string; contentStatus?: string;
    },
): Promise<ProductListResponse> {
    const sp = new URLSearchParams();
    if (params?.query) sp.set('query', normalizeDigits(params.query));
    if (params?.barcode) sp.set('barcode', normalizeDigits(params.barcode));
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.category) sp.set('category', params.category);
    if (params?.status) sp.set('status', params.status);
    if (params?.stock) sp.set('stock', params.stock);
    if (params?.hasImage) sp.set('hasImage', params.hasImage);
    if (params?.aiReady) sp.set('aiReady', params.aiReady);
    if (params?.recEligible) sp.set('recEligible', params.recEligible);
    if (params?.enrichNeeded) sp.set('enrichNeeded', params.enrichNeeded);
    if (params?.imageStatus) sp.set('imageStatus', params.imageStatus);
    if (params?.contentStatus) sp.set('contentStatus', params.contentStatus);
    const qs = sp.toString();

    return adminFetchJson<ProductListResponse>(`/admin/products${qs ? `?${qs}` : ''}`);
}

export interface QualityStats {
    total: number;
    active: number;
    inactive: number;
    aiReady: number;
    notAiReady: number;
    recEligible: number;
    needsEnrichment: number;
}

export async function getProductQualityStats(): Promise<QualityStats> {
    return adminFetchJson<QualityStats>('/admin/products/quality-stats');
}

export interface ProductCategory {
    name: string;
    count: number;
}

export async function listProductCategories(): Promise<ProductCategory[]> {
    return adminFetchJson<ProductCategory[]>('/admin/products/categories');
}

export async function getProduct(id: string): Promise<Product> {
    return adminFetchJson<Product>(`/admin/products/${id}`);
}

export async function createProduct(data: Partial<Product>): Promise<Product> {
    return adminFetchJson<Product>('/admin/products', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    return adminFetchJson<Product>(`/admin/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

// ── PRODUCT-IMAGE-ASSET-MANAGER-P1 — image upload + import-from-URL ──

export interface ProductImageUploadResult {
    ok: true;
    imageUrl: string;
    fileKey: string;
    width: number;
    height: number;
    sizeBytes: number;
}

/**
 * Read a File blob as base64 (without the `data:image/...;base64,` prefix).
 * Browser-only; called from the admin product edit page.
 */
function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== 'string') {
                reject(new Error('FileReader did not return a string'));
                return;
            }
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(file);
    });
}

/**
 * Upload a local image File to be re-encoded and stored as the product's
 * primary image. The backend re-encodes to WebP, strips EXIF, hashes by
 * sha256, and returns the new local imageUrl that has already been
 * mirrored into Product.imageUrl + the product_images primary row.
 */
export async function uploadProductImage(
    productId: string,
    file: File,
    altText?: string | null,
): Promise<ProductImageUploadResult> {
    const dataBase64 = await readFileAsBase64(file);
    return adminFetchJson<ProductImageUploadResult>(
        `/admin/products/${productId}/image/upload`,
        {
            method: 'POST',
            body: JSON.stringify({
                filename: file.name,
                mimeType: file.type,
                dataBase64,
                altText: altText ?? null,
            }),
        },
    );
}

/**
 * Import an image from an external URL. The backend SSRF-gates the host
 * (no localhost / private ranges / internal Docker hostnames; ≤3
 * redirects re-validated each hop; 5 s timeout; ≤5 MiB hard cap), then
 * runs the buffer through the same image normalization pipeline as
 * uploadProductImage. Result mirrors uploadProductImage's response.
 */
export async function importProductImageFromUrl(
    productId: string,
    url: string,
    altText?: string | null,
): Promise<ProductImageUploadResult> {
    return adminFetchJson<ProductImageUploadResult>(
        `/admin/products/${productId}/image/import-url`,
        {
            method: 'POST',
            body: JSON.stringify({
                url: url.trim(),
                altText: altText ?? null,
            }),
        },
    );
}

export async function addBarcode(productId: string, barcode: string) {
    return adminFetchJson(`/admin/products/${productId}/barcodes`, {
        method: 'POST',
        body: JSON.stringify({ barcode }),
    });
}

// ── AI Draft ────────────────────────────────────────────

export interface AiDraftRequest {
    name_ar: string;
    name_en?: string;
    brand?: string;
    category?: string;
    notes?: string;
    label_text?: string;
    market?: string;
}

export type AiDraftResponse = Record<string, unknown>;

export async function generateAiDraft(req: AiDraftRequest): Promise<AiDraftResponse> {
    return adminFetchJson('/admin/ai/product-draft', {
        method: 'POST',
        body: JSON.stringify(req),
    });
}

export async function generateEnrichmentDraft(req: AiDraftRequest): Promise<AiDraftResponse> {
    return adminFetchJson('/admin/ai/enrichment-draft', {
        method: 'POST',
        body: JSON.stringify(req),
    });
}

export interface AutofillResult {
    productId: string;
    status: 'draft' | 'complete';
    missingFields: string[];
    enrichmentScore?: number;
    generatedDraft: Record<string, unknown> | null;
}

export async function autofillEnrichment(productId: string): Promise<AutofillResult> {
    return adminFetchJson(`/admin/products/${productId}/enrichment-autofill`, {
        method: 'POST',
    });
}

export async function regenerateEnrichmentField(productId: string, field: string): Promise<{ productId: string; field: string; value: unknown }> {
    return adminFetchJson(`/admin/products/${productId}/enrichment-regenerate-field`, {
        method: 'POST',
        body: JSON.stringify({ field }),
    });
}

export interface ReadinessBreakdownEntry {
    key: string;
    label: string;
    score: number;
    max: number;
    status: 'ok' | 'missing';
}

export interface ReadinessBreakdown {
    aiReadinessScore: number;
    aiReady: boolean;
    breakdown: ReadinessBreakdownEntry[];
    topMissing: string[];
    topImpactFixes: { label: string; points: number }[];
    guidanceSummary: string;
}

export async function getReadinessBreakdown(productId: string): Promise<ReadinessBreakdown> {
    return adminFetchJson(`/admin/products/${productId}/readiness-breakdown`);
}

// ── Enrichment Quality Validation ───────────────────────

export interface EnrichmentQuality {
    score: number;
    label: 'strong' | 'acceptable' | 'weak';
    issues: string[];
    strengths: string[];
    fieldDiagnosis: Record<string, 'strong' | 'acceptable' | 'weak' | 'missing'>;
}

export async function getEnrichmentQuality(productId: string): Promise<EnrichmentQuality> {
    return adminFetchJson(`/admin/products/${productId}/enrichment-quality`);
}

export interface AdminUser {
    id: string;
    email: string;
    isActive: boolean;
    createdAt: string;
    roles: { role: { id: string; name: string } }[];
}

export async function listAdminUsers(): Promise<AdminUser[]> {
    return adminFetchJson<AdminUser[]>('/admin-auth/users');
}

export async function createAdminUser(data: { email: string; password: string; isActive?: boolean }): Promise<AdminUser> {
    return adminFetchJson<AdminUser>('/admin-auth/users', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateAdminUser(id: string, data: { isActive?: boolean; password?: string }): Promise<AdminUser> {
    return adminFetchJson<AdminUser>(`/admin-auth/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function assignUserRoles(userId: string, roleIds: string[]): Promise<void> {
    await adminFetch(`/admin-auth/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ roleIds }),
    });
}

export async function resetAdminPassword(userId: string): Promise<{ tempPassword: string }> {
    return adminFetchJson<{ tempPassword: string }>(`/admin-auth/users/${userId}/reset-password`, {
        method: 'POST',
    });
}

export async function deleteAdminUser(userId: string): Promise<void> {
    await adminFetch(`/admin-auth/users/${userId}`, {
        method: 'DELETE',
    });
}

// ── RBAC: Roles ─────────────────────────────────────────

export interface AdminRole {
    id: string;
    name: string;
    description: string | null;
    createdAt: string;
    permissions: { permission: { id: string; key: string; label: string | null } }[];
    _count: { users: number };
}

export interface AdminPermission {
    id: string;
    key: string;
    label: string | null;
}

export async function listAdminRoles(): Promise<AdminRole[]> {
    return adminFetchJson<AdminRole[]>('/admin-auth/roles');
}

export async function listPermissions(): Promise<AdminPermission[]> {
    return adminFetchJson<AdminPermission[]>('/admin-auth/roles/permissions');
}

export async function createAdminRole(data: { name: string; description?: string }): Promise<AdminRole> {
    return adminFetchJson<AdminRole>('/admin-auth/roles', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateRolePermissions(roleId: string, permissionIds: string[]): Promise<void> {
    await adminFetch(`/admin-auth/roles/${roleId}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify({ permissionIds }),
    });
}

// ── Sync API ──────────────────────────────────────────

export async function getSyncStatus(): Promise<Record<string, unknown>> {
    return adminFetchJson('/admin/sync/status');
}

export async function triggerSync(): Promise<Record<string, unknown>> {
    return adminFetchJson('/admin/sync/run', { method: 'POST' });
}

export async function getSyncRuns(limit = 20, from?: string, to?: string): Promise<{ runs: Record<string, unknown>[] }> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return adminFetchJson(`/admin/sync/runs?${params.toString()}`);
}

export interface ErpSyncResult {
    success: boolean;
    fetched?: number;
    upserted?: number;
    errors?: number;
    qualityAssessed?: number;
    durationMs?: number;
    error?: string;
}

export async function triggerErpSync(): Promise<ErpSyncResult> {
    return adminFetchJson('/admin/sync/erp-sync', { method: 'POST' });
}

export async function getSyncErrors(runId: string, limit = 50): Promise<{ errors: Record<string, unknown>[] }> {
    return adminFetchJson(`/admin/sync/errors?run_id=${runId}&limit=${limit}`);
}

export async function getLatestSyncErrors(limit = 100, type?: string): Promise<{ errors: Record<string, unknown>[] }> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (type) params.set('type', type);
    return adminFetchJson(`/admin/sync/errors/latest?${params.toString()}`);
}

// ── ERP Sync Runs ───────────────────────────────────

export interface ErpSyncRun {
    id: number;
    started_at: string;
    finished_at: string | null;
    status: string;
    trigger_type: string;
    fetched: number;
    upserted: number;
    quality_assessed: number;
    eligible: number;
    blocked: number;
    anomalies: number;
    error_count: number;
    errors: unknown[];
    duration_ms: number;
}

export async function getErpSyncRuns(params: {
    limit?: number;
    from?: string;
    to?: string;
}): Promise<{ runs: ErpSyncRun[] }> {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    return adminFetchJson(`/admin/sync/erp-runs?${query.toString()}`);
}

// ── ERP Sync Status ─────────────────────────────────

export interface ErpSyncStatusResponse {
    lastSyncedAt: string | null;
    totalProducts: number;
    eligible: number;
    blocked: number;
    costAnomalies: number;
    qualityDistribution: { perfect: number; good: number; poor: number };
    activeInQcare: number;
    withRealImage: number;
    withFallbackImage: number;
    topBlockReasons: { reason: string; count: number }[];
}

export async function getErpSyncStatus(): Promise<ErpSyncStatusResponse> {
    return adminFetchJson('/admin/sync/erp-sync-status');
}

// ── ERP Explorer ─────────────────────────────────────

export interface ErpProductRow {
    itemNumber: string;
    barcode: string | null;
    productName: string | null;
    vendorName: string | null;
    salesPrice: number | null;
    costPrice: number | null;
    tradePrice: number | null;
    retailCategoryName: string | null;
    searchName: string | null;
    countryOfOrigin: string | null;
    productGroupId: string;
    stockTahlia: number;
    stockHamdaniya: number;
    stockWarehouse: number;
    stockTotal: number;
    totalRevenue: number;
    totalQtySold: number;
    transactionCount: number;
}

export interface ErpListResponse {
    data?: ErpProductRow[];
    pagination?: { page: number; limit: number; total: number; totalPages: number };
    queryTimeMs?: number;
    error?: string;
    configured?: boolean;
}

export interface ErpSalesSummary {
    store: string;
    transactionCount: number;
    totalQtySold: number;
    totalRevenue: number;
    firstSale: string;
    lastSale: string;
}

export interface ErpTransaction {
    store: string;
    transDate: string;
    qty: number;
    price: number;
    netAmount: number;
    txnNumber: string;
}

export interface ErpDetailResponse {
    product?: Record<string, unknown>;
    barcodes?: { barcode: string; barcodeType: string; nameAr: string }[];
    stock?: { warehouseId: string; available: number; onHand: number; reserved: number }[];
    salesSummary?: ErpSalesSummary[];
    recentTransactions?: ErpTransaction[];
    columnCount?: number;
    queryTimeMs?: number;
    error?: string;
    configured?: boolean;
    itemNumber?: string;
}

export interface ErpTopSellersResponse {
    data?: {
        itemNumber: string;
        productName: string | null;
        category: string | null;
        totalQtySold: number;
        totalRevenue: number;
        transactionCount: number;
        lastSold: string;
        salesPrice: number | null;
        stockTahlia: number;
        stockHamdaniya: number;
        stockWarehouse: number;
    }[];
    queryTimeMs?: number;
    error?: string;
    configured?: boolean;
}

export interface ErpFieldReportResponse {
    group?: string;
    fields?: { field: string; label: string; filled: number; total: number; percent: number }[];
    queryTimeMs?: number;
    error?: string;
    configured?: boolean;
}

export async function getErpProducts(params: {
    page?: number;
    limit?: number;
    group?: string;
    search?: string;
    category?: string;
    warehouse?: string;
}): Promise<ErpListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.group) query.set('group', params.group);
    if (params.search) query.set('search', params.search);
    if (params.category) query.set('category', params.category);
    if (params.warehouse) query.set('warehouse', params.warehouse);
    return adminFetchJson(`/admin/sync/erp/products?${query.toString()}`);
}

export async function getErpProductDetail(itemNumber: string): Promise<ErpDetailResponse> {
    return adminFetchJson(`/admin/sync/erp/products/${itemNumber}`);
}

export async function getErpFieldReport(group?: string): Promise<ErpFieldReportResponse> {
    const query = group ? `?group=${group}` : '';
    return adminFetchJson(`/admin/sync/erp/field-report${query}`);
}

export async function getErpTopSellers(params: {
    group?: string;
    limit?: number;
}): Promise<ErpTopSellersResponse> {
    const query = new URLSearchParams();
    if (params.group) query.set('group', params.group);
    if (params.limit) query.set('limit', String(params.limit));
    return adminFetchJson(`/admin/sync/erp/top-sellers?${query.toString()}`);
}

// ── Kiosk Management ──────────────────────────────────

export interface KioskDevice {
    id: string;
    storeId: string;
    deviceId: string;
    label: string | null;
    isActive: boolean;
    lastSeenAt: string | null;
    lastIp: string | null;
    lastUserAgent: string | null;
    createdAt: string;
    updatedAt: string;
    store: {
        id: string;
        name: string;
        code: string;
        tenant: { id: string; name: string; slug: string };
    };
}

export interface KioskCreateResponse extends KioskDevice {
    kioskSecret: string;
}

export interface KioskStore {
    id: string;
    name: string;
    code: string;
    tenant: { id: string; name: string };
}

export async function listKiosks(): Promise<KioskDevice[]> {
    return adminFetchJson<KioskDevice[]>('/admin/kiosks');
}

export async function getKiosk(id: string): Promise<KioskDevice> {
    return adminFetchJson<KioskDevice>(`/admin/kiosks/${id}`);
}

export async function createKiosk(data: { storeId: string; label?: string }): Promise<KioskCreateResponse> {
    return adminFetchJson<KioskCreateResponse>('/admin/kiosks', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateKiosk(id: string, data: { label?: string; isActive?: boolean }): Promise<KioskDevice> {
    return adminFetchJson<KioskDevice>(`/admin/kiosks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function rotateKioskSecret(id: string): Promise<{ kioskId: string; deviceId: string; kioskSecret: string }> {
    return adminFetchJson(`/admin/kiosks/${id}/rotate-secret`, {
        method: 'POST',
    });
}

export async function revokeKiosk(id: string): Promise<{ kioskId: string; deviceId: string; revoked: boolean }> {
    return adminFetchJson(`/admin/kiosks/${id}/revoke`, {
        method: 'POST',
    });
}

export async function listKioskStores(): Promise<KioskStore[]> {
    return adminFetchJson<KioskStore[]>('/admin/kiosks/stores');
}

// ── Device Access Requests ────────────────────────────

export interface DeviceAccessRequest {
    id: string;
    fingerprint: string;
    userAgent: string | null;
    ip: string | null;
    status: 'pending' | 'approved' | 'rejected' | 'claimed' | 'revoked';
    kioskId: string | null;
    kioskLabel: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface DeviceAccessApproveResponse {
    requestId: string;
    status: string;
    kiosk: { id: string; deviceId: string };
    claimToken: string;
    claimExpiresAt: string;
}

export async function listDeviceAccessRequests(status?: string): Promise<DeviceAccessRequest[]> {
    const qs = status ? `?status=${status}` : '';
    return adminFetchJson<DeviceAccessRequest[]>(`/admin/device-access/requests${qs}`);
}

export async function approveDeviceAccessRequest(
    id: string,
    data: { storeId: string; label?: string; notes?: string },
): Promise<DeviceAccessApproveResponse> {
    return adminFetchJson<DeviceAccessApproveResponse>(`/admin/device-access/requests/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function rejectDeviceAccessRequest(id: string, notes?: string): Promise<{ requestId: string; status: string }> {
    return adminFetchJson(`/admin/device-access/requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
    });
}



export interface ProductEnrichment {
    id: string;
    productId: string;
    barcode: string | null;
    externalId: string | null;
    enrichmentStatus: string;
    needsManualReview: boolean;
    confidenceLevel: string;
    generatedBy: string;
    generationBatchId: string | null;
    sourceNotes: string | null;
    marketingHeadlineAr: string | null;
    marketingDescriptionAr: string | null;
    shortPitchAr: string | null;
    benefitsAr: string[];
    howToUseAr: string[];
    warningsAr: string[];
    ingredientsAr: string[];
    suitabilityAr: string | null;
    textureAr: string | null;
    scentAr: string | null;
    finishAr: string | null;
    targetUserAr: string | null;
    ragSummaryAr: string | null;
    searchKeywordsAr: string[];
    aiContextNotesAr: string | null;
    toneProfile: string;
    audienceProfile: string;
    locale: string;
    sourceFingerprint: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface EnrichmentProductView {
    product: {
        id: string;
        barcode: string;
        nameAr: string;
        nameEn: string | null;
        brand: string | null;
        category: string | null;
        description: string | null;
        imageUrl: string | null;
        price: number;
        priceWithTax: number | null;
        taxRate: number;
        keyBenefitsAr: string[];
        howToUseAr: string | null;
        warningsAr: string | null;
        ingredientsAr: string | null;
        isActive: boolean;
    };
    enrichment: ProductEnrichment | null;
    isStale: boolean;
    currentFingerprint: string;
}

export interface EnrichmentStats {
    total: number;
    draft: number;
    reviewed: number;
    approved: number;
    needsReview: number;
    stale: number;
    productsWithout: number;
}

export async function getEnrichmentStats(): Promise<EnrichmentStats> {
    return adminFetchJson<EnrichmentStats>('/admin/enrichments/stats');
}

export async function getProductEnrichment(productId: string): Promise<EnrichmentProductView> {
    return adminFetchJson<EnrichmentProductView>(`/admin/enrichments/product/${productId}`);
}

export async function saveProductEnrichment(productId: string, data: Partial<ProductEnrichment>): Promise<ProductEnrichment> {
    return adminFetchJson<ProductEnrichment>(`/admin/enrichments/product/${productId}`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateEnrichmentStatus(id: string, status: string): Promise<ProductEnrichment> {
    return adminFetchJson<ProductEnrichment>(`/admin/enrichments/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
    });
}

// ── AI Intelligence ──────────────────────────────────────

export interface AiSessionSummary {
    id: string;
    storeId: string;
    kioskId: string;
    sessionKey: string;
    entrypoint: string;
    initialDomain: string;
    totalTurns: number;
    totalProductsShown: number;
    totalProductsClicked: number;
    totalScans: number;
    startedAt: string;
    lastActivityAt: string;
    durationSeconds: number;
}

export interface AiConversationTurn {
    id: string;
    sessionId: string;
    turnIndex: number;
    userMessage: string;
    assistantResponse: string;
    detectedIntent: string;
    detectedDomain: string;
    slotsExtracted: Record<string, unknown>;
    routeType: string;
    recommendedProductIds: string[];
    recommendedProductNames: string[];
    recommendationCount: number;
    antiRepetitionApplied: boolean;
    consultationTriggered: boolean;
    companyKnowledgeTriggered: boolean;
    latencyMs: number;
    createdAt: string;
}

export interface AiProductEvent {
    id: string;
    sessionId: string;
    turnId: string | null;
    productId: string;
    productName: string;
    eventType: string;
    source: string;
    createdAt: string;
}

export interface AiSessionDetail extends AiSessionSummary {
    turns: AiConversationTurn[];
    productEvents: AiProductEvent[];
}

export interface AiDashboardSummary {
    totalSessions: number;
    totalTurns: number;
    totalProductEvents: number;
    avgTurnsPerSession: number;
    sessionsWithProducts: number;
    productRecommendationRate: number;
    topIntents: { intent: string; count: number }[];
    topDomains: { domain: string; count: number }[];
    topRouteTypes: { route: string; count: number }[];
}

export interface AiTrainingRecord {
    user_input: string;
    detected_intent: string;
    detected_domain: string;
    slots: Record<string, unknown>;
    route_type: string;
    recommended_products: string[];
    clicked_products: string[];
    assistant_response: string;
    quality_flags: string[];
    useful_for: string[];
    timestamp: string;
}

export async function getIntelligenceSummary(): Promise<AiDashboardSummary> {
    return adminFetchJson<AiDashboardSummary>('/admin/intelligence/summary');
}

export async function listAiSessions(params?: {
    page?: number; limit?: number; domain?: string;
}): Promise<{ sessions: AiSessionSummary[]; total: number; page: number; pages: number }> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.domain) sp.set('domain', params.domain);
    return adminFetchJson(`/admin/intelligence/sessions?${sp.toString()}`);
}

export async function getAiSession(id: string): Promise<AiSessionDetail> {
    return adminFetchJson<AiSessionDetail>(`/admin/intelligence/sessions/${id}`);
}

export async function listAiTurns(params?: {
    page?: number; limit?: number; intent?: string; routeType?: string;
}): Promise<{ turns: AiConversationTurn[]; total: number; page: number; pages: number }> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.intent) sp.set('intent', params.intent);
    if (params?.routeType) sp.set('routeType', params.routeType);
    return adminFetchJson(`/admin/intelligence/turns?${sp.toString()}`);
}

export async function getTopRecommendedProducts(limit = 20): Promise<{ productId: string; productName: string; count: number }[]> {
    return adminFetchJson(`/admin/intelligence/products/recommended?limit=${limit}`);
}

export async function getTopClickedProducts(limit = 20): Promise<{ productId: string; productName: string; count: number }[]> {
    return adminFetchJson(`/admin/intelligence/products/clicked?limit=${limit}`);
}

export async function getTrainingExport(limit = 100): Promise<AiTrainingRecord[]> {
    return adminFetchJson(`/admin/intelligence/training-export?limit=${limit}`);
}

// ── Mobile Customers (CRM) ──────────────────────────────

export interface MobileCustomer {
    id: string;
    phone: string;
    name: string;
    age: string | null;
    skinType: string | null;
    mainConcern: string | null;
    allergyNote: string | null;
    currentGoal: string | null;
    storeId: string | null;
    isActive: boolean;
    // QCARE-MOBILE-CUSTOMER-BLOCK (2026-05-16): admin-controlled hard block,
    // distinct from `isActive` archive flag. Audit fields are retained when
    // an admin unblocks so the forensic trail of past incidents survives.
    isBlocked?: boolean;
    blockedAt?: string | null;
    blockedReason?: string | null;
    blockedBy?: string | null;
    createdAt: string;
    updatedAt: string;
    // Lifecycle / onboarding fields — may be absent on the list endpoint
    // (which currently selects the original CRM scalar set), but always
    // present on the detail endpoint.
    phoneVerified?: boolean;
    experienceMode?: string | null;
    source?: string;
    lastSeenAt?: string | null;
    // Profile Memory v1 — durable facts surfaced on the detail page.
    hairType?: string | null;
    rejectsFragrance?: boolean;
    rejectsTreatment?: boolean;
    rejectsGel?: boolean;
    // Daily credits — raw columns + derived status. Both list and detail
    // endpoints now populate these; nextResetAt / secondsRemaining are
    // computed server-side from lastCreditResetAt (not persisted).
    dailyCredits?: number;
    remainingCredits?: number;
    lastCreditResetAt?: string | null;
    creditStatus?: CreditStatus;
    _count?: { conversationTurns: number };
}

export interface MobileCustomerListResponse {
    items: MobileCustomer[];
    total: number;
    page: number;
    limit: number;
    // QCARE-ADMIN-CUSTOMERS-PAGINATION (2026-05-17): server-derived total
    // pages. Optional so an older API build that doesn't ship it doesn't
    // break this client; frontend falls back to ceil(total / limit).
    totalPages?: number;
}

// QCARE-AI-RELEASE-CONVERSATION-UI-PAYLOAD-PERSISTENCE (2026-05-03):
// Visible UI render metadata for an assistant turn. Backend
// MobileChatService.persistTurns populates this from response.suggestions
// + response.blocks + response.meta (pinned allowlist) on AI turns.
// Always undefined/null on user turns and on plain-text AI replies.
// products stays separate — it's still the source of truth for product
// cards and is populated independently.
export interface MobileTurnUiPayload {
    version?: number;
    suggestions?: string[];
    blocks?: unknown[];
    meta?: Record<string, unknown>;
}

export interface MobileConversationTurn {
    id: string;
    role: 'user' | 'ai';
    message: string;
    products: unknown[];
    uiPayload?: MobileTurnUiPayload | null;
    createdAt: string;
}

export interface FeedbackSummary {
    likes: number;
    dislikes: number;
    total: number;
    // null when there is no feedback at all — the admin UI renders a dash
    // rather than pretending a 0% satisfaction rate exists.
    satisfaction: number | null;
}

export interface FeedbackRecord {
    id: string;
    sessionId: string;
    messageId: string;
    feedbackType: 'like' | 'dislike';
    messagePreview: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface MobileCustomerDetail extends MobileCustomer {
    conversationTurns: MobileConversationTurn[];
    feedbackSummary?: FeedbackSummary;
    recentFeedback?: FeedbackRecord[];
}

export async function listMobileCustomers(params?: {
    page?: number; limit?: number; search?: string; includeArchived?: boolean;
}): Promise<MobileCustomerListResponse> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.search) sp.set('search', params.search);
    if (params?.includeArchived) sp.set('includeArchived', 'true');
    const qs = sp.toString();
    return adminFetchJson<MobileCustomerListResponse>(`/admin/customers${qs ? `?${qs}` : ''}`);
}

export async function getMobileCustomerDetail(id: string): Promise<MobileCustomerDetail> {
    return adminFetchJson<MobileCustomerDetail>(`/admin/customers/${id}`);
}

export async function archiveMobileCustomers(ids: string[]): Promise<{ archived: number }> {
    return adminFetchJson<{ archived: number }>('/admin/customers/archive', {
        method: 'POST',
        body: JSON.stringify({ ids }),
    });
}

export async function deleteMobileCustomersPermanently(ids: string[], confirm: string): Promise<{ deleted: number }> {
    return adminFetchJson<{ deleted: number }>('/admin/customers/delete', {
        method: 'POST',
        body: JSON.stringify({ ids, confirm }),
    });
}

export interface AddCreditsResponse {
    id: string;
    dailyCredits: number;
    remainingCredits: number;
    lastCreditResetAt: string | null;
    creditStatus: CreditStatus;
    // `added` is what was actually applied after the server-side cap.
    // A misclick that tries to add 50 when the row is already at 95
    // returns added=5, not 50 — the UI should surface this to the admin.
    added: number;
}

export async function addCustomerCredits(id: string, amount: number): Promise<AddCreditsResponse> {
    return adminFetchJson<AddCreditsResponse>(`/admin/customers/${id}/add-credits`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
    });
}

export interface ResetCreditsResponse {
    id: string;
    dailyCredits: number;
    remainingCredits: number;
    lastCreditResetAt: string | null;
    creditStatus: CreditStatus;
}

export async function resetCustomerCredits(id: string): Promise<ResetCreditsResponse> {
    return adminFetchJson<ResetCreditsResponse>(`/admin/customers/${id}/reset-credits`, {
        method: 'POST',
    });
}

// ── QCARE-MOBILE-CUSTOMER-BLOCK (2026-05-16) — admin block/unblock ──
export const MOBILE_CUSTOMER_BLOCK_REASONS = [
    'prompt_injection',
    'abuse',
    'spam',
    'policy_violation',
    'manual_admin',
    'other',
] as const;
export type MobileCustomerBlockReason = typeof MOBILE_CUSTOMER_BLOCK_REASONS[number];

export interface MobileCustomerBlockStatus {
    id: string;
    isBlocked: boolean;
    blockedAt: string | null;
    blockedReason: string | null;
    blockedBy: string | null;
}

export async function getMobileCustomerBlockStatus(id: string): Promise<MobileCustomerBlockStatus> {
    return adminFetchJson<MobileCustomerBlockStatus>(`/admin/mobile-customers/${id}/block-status`);
}

export async function blockMobileCustomer(
    id: string,
    reason: MobileCustomerBlockReason,
): Promise<MobileCustomerBlockStatus> {
    return adminFetchJson<MobileCustomerBlockStatus>(`/admin/mobile-customers/${id}/block`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
}

export async function unblockMobileCustomer(id: string): Promise<MobileCustomerBlockStatus> {
    return adminFetchJson<MobileCustomerBlockStatus>(`/admin/mobile-customers/${id}/unblock`, {
        method: 'POST',
    });
}

// ── Admin Message Evaluations (Phase 1 — manual QA) ─────────

export type EvaluationVerdict = 'excellent' | 'good' | 'acceptable' | 'poor';

export interface MessageEvaluation {
    id: string;
    customerId: string;
    sessionId: string;
    messageId: string;
    userMessagePreview: string | null;
    aiMessagePreview: string | null;
    accuracyScore: number;
    relevanceScore: number;
    toneScore: number;
    policyScore: number;
    notes: string | null;
    finalVerdict: EvaluationVerdict;
    evaluatedBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface MessageEvaluationWithCustomer extends MessageEvaluation {
    customer: { id: string; name: string; phone: string };
}

export interface MessageEvaluationInput {
    sessionId: string;
    messageId: string;
    accuracyScore: number;
    relevanceScore: number;
    toneScore: number;
    policyScore: number;
    finalVerdict: EvaluationVerdict;
    notes?: string;
    userMessagePreview?: string;
    aiMessagePreview?: string;
}

export async function upsertMessageEvaluation(
    customerId: string,
    messageId: string,
    data: MessageEvaluationInput,
): Promise<MessageEvaluation> {
    return adminFetchJson<MessageEvaluation>(
        `/admin/evaluations/customer/${customerId}/message/${encodeURIComponent(messageId)}`,
        {
            method: 'PUT',
            body: JSON.stringify(data),
        },
    );
}

export async function getMessageEvaluation(
    customerId: string,
    messageId: string,
): Promise<MessageEvaluation | null> {
    const res = await adminFetchJson<{ evaluation: MessageEvaluation | null }>(
        `/admin/evaluations/customer/${customerId}/message/${encodeURIComponent(messageId)}`,
    );
    return res.evaluation;
}

export async function listCustomerEvaluations(
    customerId: string,
    limit = 50,
): Promise<MessageEvaluation[]> {
    const res = await adminFetchJson<{ items: MessageEvaluation[] }>(
        `/admin/evaluations/customer/${customerId}?limit=${limit}`,
    );
    return res.items;
}

export async function listRecentEvaluations(limit = 50): Promise<MessageEvaluationWithCustomer[]> {
    const res = await adminFetchJson<{ items: MessageEvaluationWithCustomer[] }>(
        `/admin/evaluations/recent?limit=${limit}`,
    );
    return res.items;
}

// ── Admin Config ────────────────────────────────────────────

export interface AdminConfigEntry {
    key: string;
    value: Record<string, unknown>;
    updatedAt: string | null;
    updatedBy: string | null;
}

export async function getAdminConfig(key: string): Promise<AdminConfigEntry> {
    return adminFetchJson<AdminConfigEntry>(`/admin/config/${key}`);
}

export async function updateAdminConfig(key: string, value: Record<string, unknown>): Promise<AdminConfigEntry> {
    return adminFetchJson<AdminConfigEntry>(`/admin/config/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value }),
    });
}

// ── Model Improvement Items ─────────────────────────────────

const IMPROVEMENT_ITEMS_KEY = 'model_improvement_items';

export interface ImprovementItem {
    id: string;
    customerName: string;
    customerId: string;
    userMessage: string;
    aiReply: string;
    turnTimestamp: string;
    category: 'behavior' | 'problems' | 'tone' | 'retrieval';
    note?: string;
    createdAt: string;
}

export async function listImprovementItems(): Promise<ImprovementItem[]> {
    const res = await getAdminConfig(IMPROVEMENT_ITEMS_KEY);
    const items = (res.value as { items?: ImprovementItem[] })?.items;
    return Array.isArray(items) ? items : [];
}

export async function appendImprovementItem(item: Omit<ImprovementItem, 'id' | 'createdAt'>): Promise<ImprovementItem[]> {
    const existing = await listImprovementItems();
    const newItem: ImprovementItem = {
        ...item,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
    };
    const updated = [newItem, ...existing];
    await updateAdminConfig(IMPROVEMENT_ITEMS_KEY, { items: updated });
    return updated;
}

export async function deleteImprovementItem(id: string): Promise<ImprovementItem[]> {
    const existing = await listImprovementItems();
    const updated = existing.filter(i => i.id !== id);
    await updateAdminConfig(IMPROVEMENT_ITEMS_KEY, { items: updated });
    return updated;
}

// ── R7-A1 — Comment Q&A Knowledge Bank ───────────────────
// R7-B1: same table now backs the unified "تدريب QAI" admin page;
// `kind` discriminates training-item types. Only Q&A-shaped kinds
// (qa_answer / business_fact / entity_fact) ever influence chat
// today; the others are stored as drafts/audit rows ahead of
// R7-B2 (alias resolver) and R7-B4 (recommendation rules).
export type KnowledgeQaKind =
    | 'qa_answer'
    | 'business_fact'
    | 'entity_fact'
    | 'product_alias'
    | 'recommendation_rule'
    | 'campaign_feature';

export const KNOWLEDGE_QA_KIND_LABELS: Record<KnowledgeQaKind, string> = {
    qa_answer: 'سؤال وإجابة',
    business_fact: 'معلومة عامة',
    entity_fact: 'معلومة عن جهة/حساب',
    product_alias: 'اسم منتج شائع',
    recommendation_rule: 'قاعدة ترشيح',
    campaign_feature: 'منتج حملة / عرض',
};

// Kinds that affect customer chat TODAY in production. As of
// v2.19.112 every Training Hub kind has runtime behaviour activated
// in production behind its respective env flag. Admin UI consumes
// this list to render the "active" notice on list/new/edit pages.
//
// Each kind affects chat differently:
//   * qa_answer / business_fact / entity_fact — used as trusted
//     reference knowledge injected into the LLM context.
//   * product_alias — maps colloquial names to catalog products.
//   * recommendation_rule — capped ±5 ranking delta on already-
//     eligible candidates.
//   * campaign_feature — capped +5 merchandising boost on already-
//     eligible candidates; combined-with-rule cap +8.
export const KNOWLEDGE_QA_KINDS_ACTIVE_IN_CHAT: ReadonlyArray<KnowledgeQaKind> = [
    'qa_answer',
    'business_fact',
    'entity_fact',
    'product_alias',
    'recommendation_rule',
    'campaign_feature',
];

// Legacy lists kept so existing imports keep compiling, but as of
// v2.19.112 (full R7-C activation in production) both are
// intentionally empty — every defined kind is active.
export const KNOWLEDGE_QA_KINDS_DEV_RUNTIME_PENDING_PROD: ReadonlyArray<KnowledgeQaKind> = [];
export const KNOWLEDGE_QA_KINDS_RUNTIME_NOT_LIVE: ReadonlyArray<KnowledgeQaKind> = [];

export interface KnowledgeQa {
    id: string;
    kind: KnowledgeQaKind;
    questionAr: string;
    questionEn: string | null;
    searchKeywordsAr: string[];
    answerAr: string;
    answerEn: string | null;
    linkedProductIds: string[];
    domain: string | null;
    productType: string | null;
    concerns: string[];
    safetyNotes: string | null;
    status: 'draft' | 'approved' | 'archived';
    sourcePlatform: string | null;
    sourceUrl: string | null;
    createdBy: string | null;
    approvedBy: string | null;
    approvedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface KnowledgeQaListResponse {
    rows: KnowledgeQa[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface KnowledgeQaInput {
    questionAr: string;
    questionEn?: string | null;
    searchKeywordsAr?: string[];
    answerAr: string;
    answerEn?: string | null;
    linkedProductIds?: string[];
    domain?: string | null;
    productType?: string | null;
    concerns?: string[];
    safetyNotes?: string | null;
    sourcePlatform?: string | null;
    sourceUrl?: string | null;
    kind?: KnowledgeQaKind;
}

export async function listKnowledgeQa(params: {
    status?: string;
    domain?: string;
    q?: string;
    kind?: KnowledgeQaKind | string;
    page?: number;
    limit?: number;
} = {}): Promise<KnowledgeQaListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.kind) qs.set('kind', params.kind);
    if (params.domain) qs.set('domain', params.domain);
    if (params.q) qs.set('q', params.q);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return adminFetchJson<KnowledgeQaListResponse>(`/admin/knowledge/qa${query ? `?${query}` : ''}`);
}

export async function getKnowledgeQa(id: string): Promise<KnowledgeQa> {
    return adminFetchJson<KnowledgeQa>(`/admin/knowledge/qa/${id}`);
}

export async function createKnowledgeQa(body: KnowledgeQaInput): Promise<KnowledgeQa> {
    return adminFetchJson<KnowledgeQa>('/admin/knowledge/qa', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function updateKnowledgeQa(id: string, body: Partial<KnowledgeQaInput>): Promise<KnowledgeQa> {
    return adminFetchJson<KnowledgeQa>(`/admin/knowledge/qa/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export async function approveKnowledgeQa(id: string): Promise<KnowledgeQa> {
    return adminFetchJson<KnowledgeQa>(`/admin/knowledge/qa/${id}/approve`, {
        method: 'POST',
    });
}

export async function archiveKnowledgeQa(id: string): Promise<KnowledgeQa> {
    return adminFetchJson<KnowledgeQa>(`/admin/knowledge/qa/${id}/archive`, {
        method: 'POST',
    });
}

export async function deleteKnowledgeQa(id: string): Promise<{ id: string; deleted: boolean }> {
    return adminFetchJson<{ id: string; deleted: boolean }>(`/admin/knowledge/qa/${id}`, {
        method: 'DELETE',
    });
}

// ── Product Alias Manager ──────────────────────────────
// Product-first alias CRUD. Each row is one (alias, product) pair;
// `sourceTrainingItemId` is always null for aliases created through
// this surface (they are detached from the training-item flow).

export interface ProductAliasRow {
    id: string;
    aliasText: string;
    normalizedAlias: string;
    productId: string;
    status: 'draft' | 'approved' | 'archived';
    sourceTrainingItemId: string | null;
    createdBy: string | null;
    approvedBy: string | null;
    approvedAt: string | null;
    createdAt: string;
    updatedAt: string;
    /** Set when the same normalized alias already points at a different product. */
    conflictWith?: { productId: string; nameAr: string } | null;
}

export async function listProductAliases(productId: string): Promise<ProductAliasRow[]> {
    const qs = new URLSearchParams({ productId });
    return adminFetchJson<ProductAliasRow[]>(`/admin/product-aliases?${qs.toString()}`);
}

export async function createProductAlias(body: { productId: string; aliasText: string }): Promise<ProductAliasRow> {
    return adminFetchJson<ProductAliasRow>('/admin/product-aliases', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function archiveProductAlias(id: string): Promise<ProductAliasRow> {
    return adminFetchJson<ProductAliasRow>(`/admin/product-aliases/${id}/archive`, {
        method: 'PATCH',
    });
}
